import React from 'react';
import { Box, Slider, Stack, Typography } from '@mui/material';
import type { EditingAllowance } from '../../../services/editingApi';
import type { EditList, OutputAspect, OutputResolution, ReframeMode } from '../../../utils/editing/editList';
import { OUTPUT_ASPECTS, OUTPUT_RESOLUTIONS } from '../../../utils/editing/editList';
import { ASPECT_LABELS, RESOLUTION_LABELS } from '../editStudioModel';
import { Choice, PanelHint } from './controls';

interface OutputPanelProps {
  list: EditList;
  allowance: EditingAllowance | null;
  onChange: (next: EditList) => void;
}

const RANK: Record<OutputResolution, number> = { '720p': 0, '1080p': 1, '2160p': 2 };

/** The result's frame: size, shape (a Short, a square) and what the reframe keeps. */
const OutputPanel: React.FC<OutputPanelProps> = ({ list, allowance, onChange }) => {
  const maxRank = allowance ? RANK[allowance.maxResolution] : 1;
  const reframe = list.reframe || { focusXPct: 50, focusYPct: 50, mode: 'crop' as ReframeMode };
  const setAspect = (aspect: OutputAspect) => onChange({ ...list, output: { ...list.output, aspect }, reframe: aspect === 'source' ? null : { ...reframe } });
  return (
    <Box>
      <PanelHint>How the export is framed. Reframing to 9:16 or 1:1 keeps the part of the picture around the point you choose.</PanelHint>
      <Stack spacing={1.5}>
        <Choice<OutputResolution>
          label="Size"
          value={list.output.resolution}
          options={OUTPUT_RESOLUTIONS.map((resolution) => ({ value: resolution, label: RESOLUTION_LABELS[resolution] + (RANK[resolution] > maxRank ? ' · Pro Max' : ''), disabled: RANK[resolution] > maxRank }))}
          onChange={(resolution) => onChange({ ...list, output: { ...list.output, resolution } })}
          testId="output-resolution"
        />
        <Choice<OutputAspect> label="Shape" value={list.output.aspect} options={OUTPUT_ASPECTS.map((aspect) => ({ value: aspect, label: ASPECT_LABELS[aspect] }))} onChange={setAspect} testId="output-aspect" />
        {list.output.aspect !== 'source' ? (
          <>
            <Choice<ReframeMode> label="Fit" value={reframe.mode} options={[{ value: 'crop', label: 'Fill the frame (crop the sides)' }, { value: 'pad', label: 'Fit the whole picture (bars)' }]} onChange={(mode) => onChange({ ...list, reframe: { ...reframe, mode } })} />
            {reframe.mode === 'crop' ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Keep in frame: {Math.round(reframe.focusXPct)}% across, {Math.round(reframe.focusYPct)}% down</Typography>
                <Slider size="small" min={0} max={100} value={reframe.focusXPct} onChange={(_, v) => onChange({ ...list, reframe: { ...reframe, focusXPct: v as number } })} aria-label="Focus across" />
                <Slider size="small" min={0} max={100} value={reframe.focusYPct} onChange={(_, v) => onChange({ ...list, reframe: { ...reframe, focusYPct: v as number } })} aria-label="Focus down" />
              </Box>
            ) : null}
          </>
        ) : null}
        <Choice
          label="Frame rate"
          value={list.output.fps === null ? 'source' : String(list.output.fps)}
          options={[{ value: 'source', label: 'As recorded' }, { value: '24', label: '24' }, { value: '25', label: '25' }, { value: '30', label: '30' }, { value: '60', label: '60' }]}
          onChange={(value) => onChange({ ...list, output: { ...list.output, fps: value === 'source' ? null : Number(value) } })}
        />
      </Stack>
    </Box>
  );
};

export default OutputPanel;
